import { useState, useEffect, useCallback } from 'react';

interface UsePullToRefreshOptions {
  onRefresh: () => Promise<void>;
  isEnabled?: boolean;
  threshold?: number;
}

interface PullToRefreshState {
  isPulling: boolean;
  pullDistance: number;
  isRefreshing: boolean;
}

/**
 * Hook for implementing pull-to-refresh functionality
 *
 * @param options.onRefresh - Async function to call when refresh is triggered
 * @param options.isEnabled - Whether pull-to-refresh is enabled (default: true)
 * @param options.threshold - Distance in pixels to trigger refresh (default: 80)
 * @returns Object containing pull state and indicator component props
 */
export function usePullToRefresh({
  onRefresh,
  isEnabled = true,
  threshold = 80,
}: UsePullToRefreshOptions): PullToRefreshState {
  const [isPulling, setIsPulling] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleRefresh = useCallback(async () => {
    if (isRefreshing || !isEnabled) return;

    setIsRefreshing(true);
    try {
      await onRefresh();
    } catch (error) {
      console.error('Pull-to-refresh error:', error);
    } finally {
      setIsRefreshing(false);
    }
  }, [onRefresh, isRefreshing, isEnabled]);

  useEffect(() => {
    if (!isEnabled) return;

    let startY = 0;
    let currentY = 0;

    const handleTouchStart = (e: TouchEvent) => {
      // Only enable pull-to-refresh if we're at the top of the page
      if (window.scrollY === 0) {
        startY = e.touches[0].clientY;
        setIsPulling(true);
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!isPulling) return;

      currentY = e.touches[0].clientY;
      const distance = currentY - startY;

      // Only track downward pulls
      if (distance > 0 && window.scrollY === 0) {
        // Prevent default to avoid scrolling bounce
        e.preventDefault();
        // Apply resistance to the pull (diminishing returns)
        const resistedDistance = Math.min(distance * 0.5, threshold * 1.5);
        setPullDistance(resistedDistance);
      }
    };

    const handleTouchEnd = () => {
      if (isPulling) {
        setIsPulling(false);

        // Trigger refresh if pulled past threshold
        if (pullDistance >= threshold) {
          handleRefresh();
        }

        // Reset pull distance with animation
        setPullDistance(0);
      }
    };

    // Add event listeners
    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isPulling, pullDistance, threshold, handleRefresh, isEnabled]);

  return {
    isPulling,
    pullDistance,
    isRefreshing,
  };
}
